import { useState } from 'react';
import InsightCard from './InsightCard';
import { comparePlayers } from '../../utils/matchStats';

const InsightsView = ({ overallInsights, allPlayers, overallStats = {}, eventMvpCounts = {}, archivedSessions = [], onOpenPastEvents }) => {
  const [activeTab, setActiveTab] = useState('recent');

  const { allTime, recent, longestWinningStreak, recentNGames } = overallInsights || {};

  // Check if any sessions have been recorded
  const hasRecordedSessions = archivedSessions.length > 0;

  // Helper function to extract player name from pair string
  const getPlayerFromPair = (pair) => {
    if (!pair || pair === 'N/A') return null;
    const players = pair.split(' & ');
    return players[0]; // Return first player for avatar
  };

  // Calculate All Time insights from overallStats and eventMvpCounts
  const calculateAllTimeInsights = () => {
    let mostMatchWins = { player: 'N/A', wins: -1 };
    let mostMVPs = { player: 'N/A', mvpCount: -1 };
    let mostEventWins = { player: 'N/A', eventMvpCount: -1 };

    // Most Match Wins: the overall standings leader (shared #34 order: W -> Win % -> game diff)
    const [leader] = Object.entries(overallStats)
      .map(([player, stats]) => ({ name: player, ...stats }))
      .sort(comparePlayers);
    if (leader && (leader.w || 0) > 0) {
      mostMatchWins = { player: leader.name, wins: leader.w };
    }

    // Calculate Most MVPs (rotation session MVPs)
    Object.entries(overallStats).forEach(([player, stats]) => {
      const mvpCount = stats.mvp ?? stats.mvpCount ?? 0;
      if (mvpCount > mostMVPs.mvpCount) {
        mostMVPs = { player, mvpCount };
      } else if (mvpCount === mostMVPs.mvpCount && mvpCount > 0) {
        // Tie-breaker: alphabetically first
        if (mostMVPs.player === 'N/A' || player.localeCompare(mostMVPs.player) < 0) {
          mostMVPs = { player, mvpCount };
        }
      }
    });

    // Calculate Most Event Wins (Event MVPs)
    Object.entries(eventMvpCounts).forEach(([player, eventMvpCount]) => {
      const count = eventMvpCount || 0;
      if (count > mostEventWins.eventMvpCount) {
        mostEventWins = { player, eventMvpCount: count };
      } else if (count === mostEventWins.eventMvpCount && count > 0) {
        // Tie-breaker: alphabetically first
        if (mostEventWins.player === 'N/A' || player.localeCompare(mostEventWins.player) < 0) {
          mostEventWins = { player, eventMvpCount: count };
        }
      }
    });

    if (mostMatchWins.wins < 0) mostMatchWins = { player: 'N/A', wins: 0 };
    if (mostMVPs.mvpCount < 0) mostMVPs = { player: 'N/A', mvpCount: 0 };
    if (mostEventWins.eventMvpCount < 0) mostEventWins = { player: 'N/A', eventMvpCount: 0 };

    return { mostMatchWins, mostMVPs, mostEventWins };
  };

  const allTimeInsights = calculateAllTimeInsights();
  
  // Get insights based on active tab
  const insights = activeTab === 'recent' ? recent : allTime;

  if (!insights) {
    return (
      <div className="card">
        {hasRecordedSessions && (
          <div className="tabs-container">
            <div className="tabs-group">
              <div 
                className={`tab ${activeTab === 'recent' ? 'tab-selected' : 'tab-unselected'}`}
                onClick={() => setActiveTab('recent')}
              >
                <div className="tab-contents">
                  <span className="tab-label">RECENT</span>
                </div>
                {activeTab === 'recent' && <div className="tab-indicator" />}
              </div>
              <div 
                className={`tab ${activeTab === 'allTime' ? 'tab-selected' : 'tab-unselected'}`}
                onClick={() => setActiveTab('allTime')}
              >
                <div className="tab-contents">
                  <span className="tab-label">HALL OF FAME</span>
                </div>
                {activeTab === 'allTime' && <div className="tab-indicator" />}
              </div>
            </div>
          </div>
        )}
        <div className="card-content text-center" style={{ padding: 'var(--spacing-xl)' }}>
          <p className="card-content body-large" style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--primary)' }}>
            No insights available yet
          </p>
          <p className="card-content body-small">
            Insights require players to meet minimum game thresholds (typically 5 games) to ensure validity.
          </p>
        </div>
      </div>
    );
  }

  // Recent tab insights
  const hasDeadlyDuo = activeTab === 'recent' && insights.deadlyDuo && insights.deadlyDuo.pair !== 'N/A';
  const hasAceMachine = activeTab === 'recent' && insights.aceMachine && insights.aceMachine.player !== 'N/A';
  const hasFaultyService = activeTab === 'recent' && insights.faultyService && insights.faultyService.player !== 'N/A';
  const hasHotStreak = activeTab === 'recent' && insights.hotStreak && insights.hotStreak.player !== 'N/A';
  const coldStreak = insights.coldStreak;
  const hasColdStreak = activeTab === 'recent' && coldStreak && coldStreak.player !== 'N/A';
  const hasOnTheRise = activeTab === 'recent' && insights.onTheRise && insights.onTheRise.player !== 'N/A';
  
  // All Time tab insights
  const hasAllTimeDeadlyDuo = activeTab === 'allTime' && insights.deadlyDuo && insights.deadlyDuo.pair !== 'N/A';
  const hasAllTimeAceMachine = activeTab === 'allTime' && insights.aceMachine && insights.aceMachine.player !== 'N/A';
  const hasWinningStreak = activeTab === 'allTime' && longestWinningStreak && longestWinningStreak.player !== 'N/A' && longestWinningStreak.streak > 0;
  const hasMostMatchWins = activeTab === 'allTime' && allTimeInsights.mostMatchWins.player !== 'N/A' && allTimeInsights.mostMatchWins.wins > 0;
  const hasMostMVPs = activeTab === 'allTime' && allTimeInsights.mostMVPs.player !== 'N/A' && allTimeInsights.mostMVPs.mvpCount > 0;
  const hasMostEventWins = activeTab === 'allTime' && allTimeInsights.mostEventWins.player !== 'N/A' && allTimeInsights.mostEventWins.eventMvpCount > 0;

  // Cascade order for the card entrance motion (one counter per tab)
  let recentIndex = 0;
  let hallIndex = 0;

  const hasAnyInsight = hasDeadlyDuo || hasAceMachine || hasFaultyService || 
                        hasHotStreak || hasColdStreak || hasOnTheRise ||
                        hasAllTimeDeadlyDuo || hasAllTimeAceMachine || hasWinningStreak || 
                        hasMostMatchWins || hasMostMVPs || hasMostEventWins;

  if (!hasAnyInsight) {
    return (
      <div className="card">
        {hasRecordedSessions && (
          <div className="tabs-container">
            <div className="tabs-group">
              <div 
                className={`tab ${activeTab === 'recent' ? 'tab-selected' : 'tab-unselected'}`}
                onClick={() => setActiveTab('recent')}
              >
                <div className="tab-contents">
                  <span className="tab-label">RECENT</span>
                </div>
                {activeTab === 'recent' && <div className="tab-indicator" />}
              </div>
              <div 
                className={`tab ${activeTab === 'allTime' ? 'tab-selected' : 'tab-unselected'}`}
                onClick={() => setActiveTab('allTime')}
              >
                <div className="tab-contents">
                  <span className="tab-label">HALL OF FAME</span>
                </div>
                {activeTab === 'allTime' && <div className="tab-indicator" />}
              </div>
            </div>
          </div>
        )}
        <div className="card-content text-center" style={{ padding: 'var(--spacing-xl)' }}>
          <p className="card-content body-large" style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--primary)' }}>
            No insights available yet
          </p>
          <p className="card-content body-small">
            Insights require players to meet minimum game thresholds (typically 5 games) to ensure validity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-lg">
      {hasRecordedSessions && (
        <div className="tabs-container" style={{ '--tab-count': '2' }}>
          <div className="tabs-group">
            <div 
              className={`tab ${activeTab === 'recent' ? 'tab-selected' : 'tab-unselected'}`}
              onClick={() => setActiveTab('recent')}
            >
              <div className="tab-contents">
                <span className="tab-label">RECENT</span>
              </div>
            </div>
            <div 
              className={`tab ${activeTab === 'allTime' ? 'tab-selected' : 'tab-unselected'}`}
              onClick={() => setActiveTab('allTime')}
            >
              <div className="tab-contents">
                  <span className="tab-label">HALL OF FAME</span>
              </div>
            </div>
            {/* Single sliding indicator */}
            <div 
              className="tab-indicator"
              style={{
                left: activeTab === 'recent' ? '0%' : '50%',
                width: '50%'
              }}
            />
          </div>
        </div>
      )}
      <div className="card-content" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)', padding: 'var(--spacing-md) 0 0', position: 'relative', minHeight: '200px' }}>
        <div 
          key="recent"
          className={`tab-content ${activeTab === 'recent' ? 'tab-content-active' : 'tab-content-inactive'}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)', alignSelf: 'stretch' }}
        >
          <div
            key={activeTab === 'recent' ? 'recent-on' : 'recent-off'}
            className={`insights-cascade ${activeTab === 'recent' ? 'insights-play' : ''}`}
          >
              {/* Deadly Duo (Recent) */}
              {hasDeadlyDuo && (
                <InsightCard
                  title="Deadly Duo"
                  playerName={insights.deadlyDuo.pair}
                  icon="swords"
                  iconBgColor="#7631D6"
                  subtext={`${insights.deadlyDuo.wins} Wins | ${(insights.deadlyDuo.winPercentage * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={recentIndex++}
                />
              )}

              {/* Hot Streak (Recent only) */}
              {hasHotStreak && (
                <InsightCard
                  title="Hot Streak"
                  playerName={insights.hotStreak.player}
                  icon="mode_heat"
                  iconBgColor="#DF582B"
                  subtext={`${insights.hotStreak.wins} Wins | ${(insights.hotStreak.winRate * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={recentIndex++}
                />
              )}

              {/* Cold Streak (Recent only) */}
              {hasColdStreak && (
                <InsightCard
                  title="Cold Streak"
                  playerName={insights.coldStreak.player}
                  icon="ac_unit"
                  iconBgColor="#31B3D6"
                  subtext={`${(insights.coldStreak.winRate * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={recentIndex++}
                />
              )}

              {/* Ace Machine (Recent) */}
              {hasAceMachine && (
                <InsightCard
                  title="Ace Machine"
                  playerName={insights.aceMachine.player}
                  icon="padel"
                  iconBgColor="#2569E8"
                  subtext={`${insights.aceMachine.wins} Wins as Server | ${(insights.aceMachine.winRate * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={recentIndex++}
                />
              )}

              {/* Faulty Service (Recent only) */}
              {hasFaultyService && (
                <InsightCard
                  title="Faulty Service"
                  playerName={insights.faultyService.player}
                  icon="build"
                  iconBgColor="#C88921"
                  subtext={`${insights.faultyService.losses} Losses as Server | ${(insights.faultyService.winRate * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={recentIndex++}
                />
              )}


              {/* On the Rise (Recent only) */}
              {hasOnTheRise && (
                <InsightCard
                  title="On the Rise"
                  playerName={insights.onTheRise.player}
                  icon="trending_up"
                  iconBgColor="#9C27B0"
                  subtext={`Improvement: +${(insights.onTheRise.improvement * 100).toFixed(1)}%`}
                  allPlayers={allPlayers}
                  index={recentIndex++}
                />
              )}

              {/* Footer text for Recent tab */}
              <p className="body-small" style={{ textAlign: 'center', paddingTop: 'var(--spacing-sm)', color: 'var(--on-surface-variant)' }}>
                Recent insights are based on the last {recentNGames || 50} games
              </p>
          </div>
        </div>
        <div 
          key="allTime"
          className={`tab-content ${activeTab === 'allTime' ? 'tab-content-active' : 'tab-content-inactive'}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)', alignSelf: 'stretch' }}
        >
          <div
            key={activeTab === 'allTime' ? 'hall-on' : 'hall-off'}
            className={`insights-cascade ${activeTab === 'allTime' ? 'insights-play' : ''}`}
          >
              {/* Winning Streak (All Time - Gold) */}
              {hasWinningStreak && (
                <InsightCard
                  title="Winning Streak"
                  playerName={longestWinningStreak.player}
                  icon="rocket_launch"
                  iconBgColor="#F5DD7D"
                  subtext={`${longestWinningStreak.streak} wins in a row`}
                  allPlayers={allPlayers}
                  index={hallIndex++}
                  isGold={true}
                />
              )}

              {/* Most MVPs (All Time - Gold) */}
              {hasMostMVPs && (
                <InsightCard
                  title="Most MVPs"
                  playerName={allTimeInsights.mostMVPs.player}
                  icon="auto_awesome"
                  iconBgColor="#F5DD7D"
                  subtext={`${allTimeInsights.mostMVPs.mvpCount} MVP wins`}
                  allPlayers={allPlayers}
                  index={hallIndex++}
                  isGold={true}
                />
              )}

              {/* Most Match Wins (All Time - Gold) */}
              {hasMostMatchWins && (
                <InsightCard
                  title="Most Match Wins"
                  playerName={allTimeInsights.mostMatchWins.player}
                  icon="emoji_events"
                  iconBgColor="#F5DD7D"
                  subtext={`${allTimeInsights.mostMatchWins.wins} match wins`}
                  allPlayers={allPlayers}
                  index={hallIndex++}
                  isGold={true}
                />
              )}

              {/* Deadly Duo (All Time - Gold) */}
              {hasAllTimeDeadlyDuo && (
                <InsightCard
                  title="Deadly Duo"
                  playerName={insights.deadlyDuo.pair}
                  icon="swords"
                  iconBgColor="#F5DD7D"
                  subtext={`${insights.deadlyDuo.wins} Wins | ${(insights.deadlyDuo.winPercentage * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={hallIndex++}
                  isGold={true}
                />
              )}

              {/* Most Event Wins (All Time - Gold) */}
              {hasMostEventWins && (
                <InsightCard
                  title="Most Event Wins"
                  playerName={allTimeInsights.mostEventWins.player}
                  icon="celebration"
                  iconBgColor="#F5DD7D"
                  subtext={`${allTimeInsights.mostEventWins.eventMvpCount} event ${allTimeInsights.mostEventWins.eventMvpCount === 1 ? 'win' : 'wins'}`}
                  allPlayers={allPlayers}
                  index={hallIndex++}
                  isGold={true}
                  onClick={onOpenPastEvents}
                />
              )}

              {/* Ace Machine (All Time - Gold) */}
              {hasAllTimeAceMachine && (
                <InsightCard
                  title="Ace Machine"
                  playerName={insights.aceMachine.player}
                  icon="padel"
                  iconBgColor="#F5DD7D"
                  subtext={`${insights.aceMachine.wins} Wins as Server | ${(insights.aceMachine.winRate * 100).toFixed(1)}% Win Rate`}
                  allPlayers={allPlayers}
                  index={hallIndex++}
                  isGold={true}
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightsView;

